import { useEffect, useState } from 'react'
import { Baby, Check, Plus, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { Button, Card, Field, Input, Select } from '../ui/kit'
import type { ChildRate, ChildRateScope } from '../lib/pricing'

/**
 * Kinderermäßigung nach Alter.
 *
 * „Kinder unter 6 Jahren kostenlos, unter 14 Jahren 50 %" — so steht es in fast jedem
 * Prospekt, und genau so wird es hier eingegeben. Die Grenze ist deshalb
 * ausschließlich formuliert („unter 6"), nicht als Spanne („0 bis 5"): der Vermieter
 * soll den Satz eintippen können, den er ohnehin schon geschrieben hat.
 *
 * Worauf die Ermäßigung wirkt, entscheidet die Betriebsart und nicht wir: in einer
 * Ferienwohnung kostet die Wohnung, und nur wer über die enthaltene Belegung hinaus
 * mitreist, zahlt extra — dort wird der Aufpreis ermäßigt. In einer Pension kostet
 * die Übernachtung je Person, und „Kinder zahlen 50 %" meint die Hälfte davon. Die
 * erste Fassung dieser Karte kannte nur den ersten Fall und rechnete am halben Markt
 * vorbei; deshalb steht die Wahl jetzt ganz oben und nicht im Kleingedruckten.
 */
export default function ChildRatesCard() {
  const { lang, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [rates, setRates] = useState<ChildRate[]>(store.db.tenant.child_rates ?? [])
  const [scope, setScope] = useState<ChildRateScope>(
    store.db.tenant.child_rate_scope ?? 'surcharge')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setRates(store.db.tenant.child_rates ?? [])
    setScope(store.db.tenant.child_rate_scope ?? 'surcharge')
  }, [store.db.tenant])

  const set = (i: number, patch: Partial<ChildRate>) => {
    setRates(rates.map((r, j) => (j === i ? { ...r, ...patch } : r)))
    setSaved(false)
  }

  const save = async () => {
    // Sortiert speichern: die Preisrechnung nimmt die erste passende Stufe, und eine
    // Liste in der falschen Reihenfolge wäre stillschweigend eine andere Regel.
    const clean = rates
      .filter((r) => r.upToAge >= 1 && r.upToAge <= 18)
      .sort((a, b) => a.upToAge - b.upToAge)
    await store.saveTenant({ child_rates: clean, child_rate_scope: scope })
    setRates(clean)
    setSaved(true)
  }

  // Ein Beispiel mit echten Zahlen des Betriebs: abstrakte Prozentsätze überzeugen
  // niemanden, „statt € 25 nur € 12,50" schon.
  const unit = store.db.units[0]
  const sample = scope === 'nightly'
    ? (unit && unit.base_occupancy > 0
        ? Math.round((unit.base_price / unit.base_occupancy) * 100) / 100
        : 0)
    : (unit?.extra_person_price ?? 0)

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <Baby size={17} />
        {de ? 'Kinderermäßigung' : 'Children’s rates'}
      </h2>
      {/* Die wichtigere der beiden Einstellungen steht zuerst: ohne sie rechnet die
          schönste Altersstaffel am Betrieb vorbei. */}
      <Field
        label={de ? 'Die Ermäßigung gilt für' : 'The discount applies to'}
        hint={scope === 'nightly'
          ? (de
              ? 'Der Nächtigungspreis wird durch die enthaltene Belegung geteilt; vom Anteil des Kindes wird abgezogen. Der Abzug steht als eigene Zeile auf der Rechnung.'
              : 'The nightly rate is divided by the included occupancy and the child’s share is reduced. The reduction appears as its own line on the invoice.')
          : (de
              ? 'Der Grundpreis bleibt unverändert — nur wer über der enthaltenen Belegung mitreist, zahlt ermäßigt.'
              : 'The base rate is untouched — only guests beyond the included occupancy pay a reduced surcharge.')}
      >
        <Select value={scope}
          onChange={(e) => { setScope(e.target.value as ChildRateScope); setSaved(false) }}>
          <option value="surcharge">
            {de
              ? 'Aufpreis je Zusatzperson (Ferienwohnung, Haus)'
              : 'Surcharge per extra person (apartment, house)'}
          </option>
          <option value="nightly">
            {de
              ? 'Übernachtung je Person (Pension, Doppelzimmer)'
              : 'Overnight rate per person (guesthouse, double room)'}
          </option>
        </Select>
      </Field>

      {rates.length === 0 && (
        <p className="text-sm text-muted">
          {de
            ? 'Noch keine Stufe hinterlegt — jede Person zahlt denselben Preis, unabhängig vom Alter.'
            : 'No bands yet — every guest pays the same, whatever their age.'}
        </p>
      )}

      {/*
        Eine Stufe ist ein eigener Block, nicht eine Zeile aus drei Teilen.
        In einer Zeile hingen die beiden Beschriftungen auf verschiedenen Höhen,
        sobald eine davon einen Hinweis darunter hatte, und der Papierkorb rutschte
        mit. Zwei gleich hohe Felder nebeneinander, Beispiel und Papierkorb in der
        Zeile darunter — das steht auf jedem Bildschirm gerade.
      */}
      {rates.map((r, i) => (
        <div key={i} className="rounded-xl bg-surface-2 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Kinder unter … Jahren' : 'Children under …'}>
              <Input type="number" min={1} max={18} value={r.upToAge}
                onChange={(e) => set(i, { upToAge: Number(e.target.value) })} />
            </Field>
            <Field label={de ? 'zahlen … %' : 'pay … %'}>
              <Input type="number" min={0} max={100} value={r.percent}
                onChange={(e) => set(i, { percent: Number(e.target.value) })} />
            </Field>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted">
            <span className="flex-1 min-w-0 truncate">
              {r.percent <= 0
                ? (de ? 'kostenfrei' : 'free of charge')
                : sample > 0
                  ? `${formatMoney(sample)} → ${formatMoney(Math.round(sample * r.percent) / 100)}`
                  : ''}
            </span>
            <button
              className="text-muted hover:text-danger shrink-0"
              aria-label={de ? 'Stufe entfernen' : 'Remove band'}
              onClick={() => { setRates(rates.filter((_, j) => j !== i)); setSaved(false) }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}

      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => {
          // Vorbelegt mit dem, was am häufigsten vorkommt: erst kostenfrei, dann halb.
          const next: ChildRate = rates.length === 0
            ? { upToAge: 6, percent: 0 }
            : { upToAge: Math.min(18, (rates[rates.length - 1]?.upToAge ?? 6) + 8), percent: 50 }
          setRates([...rates, next])
          setSaved(false)
        }}>
          <Plus size={16} /> {de ? 'Stufe hinzufügen' : 'Add a band'}
        </Button>
        <Button variant="primary" onClick={save}>
          {saved
            ? <><Check size={16} /> {de ? 'Gespeichert' : 'Saved'}</>
            : (de ? 'Speichern' : 'Save')}
        </Button>
      </div>

      {rates.length > 0 && (
        <p className="text-xs text-muted">
          {de
            ? 'Wer älter ist als die höchste Stufe, zahlt voll. Ohne Geburtsjahr in der Buchung wird ebenfalls voll gerechnet — der Online-Check-in trägt es automatisch nach.'
            : 'Anyone older than the highest band pays in full. Without ages on the booking the full rate applies too — online check-in fills them in automatically.'}
        </p>
      )}
    </Card>
  )
}
